import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ClipboardCheck, FileText, Calendar, Eye, ArrowRight } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: ClipboardCheck,
    title: 'Property Assessment',
    description: 'We begin with a detailed evaluation of your property to understand its condition, priorities, and maintenance needs.',
    details: [
      'On-site property inspection',
      'Review of current maintenance practices',
      'Discussion of priorities and concerns',
      'Identification of immediate needs and long-term improvements'
    ]
  },
  {
    number: '02',
    icon: FileText,
    title: 'Maintenance Program Development',
    description: 'Based on the assessment, we create a structured maintenance program tailored to your property.',
    details: [
      'Detailed scope of services',
      'Scheduled maintenance calendar',
      'Clear pricing and terms',
      'Defined communication protocols'
    ]
  },
  {
    number: '03',
    icon: Calendar,
    title: 'Scheduled Service & Documentation',
    description: 'Our team performs scheduled visits, documents completed work, and reports on property conditions.',
    details: [
      'Scheduled service visits',
      'Quality assurance checks',
      'Regular progress reports',
      'Direct communication and service coordination'
    ]
  },
  {
    number: '04',
    icon: Eye,
    title: 'Ongoing Property Oversight',
    description: 'Your property is continuously monitored for seasonal needs, condition changes, and improvement opportunities.',
    details: [
      'Routine property inspections',
      'Seasonal maintenance adjustments',
      'Proactive issue identification',
      'Recommendations for long-term improvements'
    ]
  }
];

// Hero Section
const HowWeWorkHero = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="how-we-work-hero">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-white mb-4 uppercase">
        How We Work
      </h1>
      <p className="text-lg text-slate-300 max-w-2xl">
        A structured exterior maintenance process built around planning, execution, and accountability.
      </p>
    </div>
  </section>
);

// Steps Section
const StepsSection = () => (
  <section className="bg-slate-50 py-20" data-testid="steps-section">
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <p className="text-lg text-slate-600 mb-16 max-w-2xl">
        Our process is designed to provide consistent care, clear communication, and long-term property protection.
      </p>
      <div className="space-y-20">
        {steps.map((step, index) => (
          <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#1a2e44]">
                  {step.number}
                </span>
                <step.icon className="w-10 h-10 text-[#1a2e44]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
                {step.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {step.description}
              </p>
            </div>

            <div className="bg-white p-6 border border-slate-200 border-t-4 border-t-[#1a2e44]">
              <ul className="space-y-4">
                {step.details.map((detail, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#1a2e44] rounded-full mt-2 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Visual Divider
const Divider = () => (
  <div className="bg-slate-50 py-8">
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <div className="w-full h-px bg-slate-300" />
    </div>
  </div>
);

// Service Area Section (divider before footer)
const ServiceAreaSection = () => (
  <section className="bg-slate-50 py-16" data-testid="service-area-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
        Service Area
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Serving Pasadena, Arcadia, San Marino, La Cañada Flintridge, and communities throughout Los Angeles County.
      </p>
    </div>
  </section>
);

// CTA Section
const HowWeWorkCTA = () => (
  <section className="bg-[#1a2e44] py-16" data-testid="how-we-work-cta">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-white mb-4">
        Schedule a Property Assessment
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        The first step is a site assessment where we evaluate your property and develop a maintenance program.
      </p>
      <Link
        to="/contact?intent=assessment"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="how-we-work-cta-btn"
      >
        Request a Site Assessment
      </Link>
    </div>
  </section>
);

export default function HowWeWorkPage() {
  useEffect(() => {
    document.title = 'How We Work | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="how-we-work-page">
      <HowWeWorkHero />
      <StepsSection />
      <Divider />
      <HowWeWorkCTA />
      <ServiceAreaSection />
    </div>
  );
}
